import { useEffect, useState, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { getCollabEditProvider } from '@confluence/fabric-providers/entry-points/NativeCollabProvider.ts';
import { getDraft } from '@confluence/fabric-providers/entry-points/getDraft.ts';
import { useFetchUsersClient } from '@confluence/fabric-providers/entry-points/useFetchUsersClient.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { useNativeCollabPresenceState } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useNativeCollabActions } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import {
	fetchCollabPermissionToken,
	nativeCollabPermissionsTokenState,
} from '@confluence/native-collab/entry-points/CollabPermissionsToken.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { preloadLivePageRoute } from '@confluence/live-page-route-preloader/entry-points/preloadLivePageRoute.ts';
import { startLivePageLoadMetrics } from '@confluence/live-pages-perf/entry-points/live-pages-perf.config.ts';

import { useHasUnsavedChanges } from '../EditorNavigationLockout.tsx';

export const useLiveDocTransitionCallback = ({
	contentId,
	collabEditProvider,
	isPublishingContent,
	isFirstParticipant,
	editorPresetAPI,
	isLivePage,
	hasEditPermission,
	isFastLoadedLiveDoc,
}) => {
	const hasUnsavedChanges = useHasUnsavedChanges({
		contentId,
		isPublishingContent,
		isFirstParticipant,
		editorPresetAPI,
	});

	// Create a promise that resolves when we're no longer waiting for unsaved changes. The latest promise will always be used by the transition callback when it's invoked, to ensure we don't transition until there are no unsaved changes.
	// Holds the resolve function for the current pending promise, so we can resolve it from the useEffect when hasUnsavedChanges becomes false.
	const isWaitingForUnsavedChangesPromiseResolveRef = useRef<
		((value: void | PromiseLike<void>) => void) | null
	>(null);
	// Holds the promise itself, which the transition callback awaits. Resolved when there are no unsaved changes.
	const isWaitingForUnsavedChangesPromiseRef = useRef<Promise<void> | null>(null);

	useEffect(() => {
		// Resolve the promise once hasUnsavedChanges is false if it didn't immediately resolve
		if (!hasUnsavedChanges && isWaitingForUnsavedChangesPromiseResolveRef.current) {
			isWaitingForUnsavedChangesPromiseResolveRef.current();
			isWaitingForUnsavedChangesPromiseResolveRef.current = null;
		}
		// Every time hasUnsavedChanges changes to true, we need to update
		// isWaitingForUnsavedChangesPromiseRef.current in order to ensure
		// we wait for these unsaved changes.
		// Without updating isWaitingForUnsavedChangesPromiseRef, the promise ref
		// may think there are no unsaved changes if `hasUnsavedChanges` was originally `false`
		// but has since changed to `true`.
		if (hasUnsavedChanges && expValEquals('platform_synced_block', 'isEnabled', true)) {
			isWaitingForUnsavedChangesPromiseRef.current = new Promise((resolve) => {
				isWaitingForUnsavedChangesPromiseResolveRef.current = resolve;
			});
		}
	}, [hasUnsavedChanges]);
	if (!isWaitingForUnsavedChangesPromiseRef.current) {
		isWaitingForUnsavedChangesPromiseRef.current = new Promise((resolve) => {
			if (!hasUnsavedChanges) {
				resolve();
			} else {
				isWaitingForUnsavedChangesPromiseResolveRef.current = resolve;
			}
		});
	}

	const { client: fetchUsersClient } = useFetchUsersClient();
	const { setCollabEditProvider, setHasInitialDraft, resetNativeCollabState } =
		useNativeCollabActions();
	const { nativeEditorComponentsPreloader } = useEditPageLoadingActions();
	const { userId, cloudId, activationId } = useSessionData();
	const spaceKey = useSpaceKey();
	const [{ presenceId }] = useNativeCollabPresenceState();
	const presenceIdRef = useRef(presenceId);
	presenceIdRef.current = presenceId;

	const [isTransitionPreloading, setIsTransitionPreloading] = useState(false);

	useEffect(() => {
		if (!isLivePage || !fg('confluence_frontend_transition_preloading_system')) return;
		getGlobalTransitionPreloader().registerTransitionCallback({
			id: 'live_doc_preloading_transition_callback_id',
			callback: ({ match }) =>
				new Promise<void>(async (resolve) => {
					setIsTransitionPreloading(true);
					try {
						if (match?.params?.contentId) {
							// TODO When updating this preloading callback to run on non-livedoc routes as we transition into a live doc:
							//      Also preload the live doc components. Reference how LivePagePreloader.tsx does this

							// We destroy collab provider as early as possible to reduce the risk of rare race conditions where
							//    we end up on a different page than expected after awaiting below promises, and then problematically destroy collab edit provider
							if (collabEditProvider) {
								collabEditProvider?.destroy?.();
								// Clear the memoized function. If the user goes back to editing the same page,
								// then we don't run into problem trying to talk to the old (destroyed) collab provider.
								if (getCollabEditProvider?.cache?.clear) {
									getCollabEditProvider.cache.clear();
								}
							}
							resetNativeCollabState();
							nativeCollabPermissionsTokenState.clearPermissionsTokenState(contentId);

							const preloadLivePageRoutePromise = preloadLivePageRoute(match, match.url);
							await Promise.all([preloadLivePageRoutePromise, nativeEditorComponentsPreloader()]);

							// Ensure we're not waiting for unsaved changes before unblocking transition
							if (isWaitingForUnsavedChangesPromiseRef.current) {
								await isWaitingForUnsavedChangesPromiseRef.current;
							}
						}
					} catch (err) {
						getLogger('useLiveDocTransitionCallback').error`Error preloading live doc: ${err}`;
					}

					setIsTransitionPreloading(false);
					resolve();
				}),
			onlyCalledIfFunc: ({ match }) => {
				// Don't seamlessly transition if the current live doc is not fast loaded, as non-fast-loaded -> fast-loaded live docs
				//   trigger a remount that breaks NCS on seamless transition
				if (match?.params?.contentId && isFastLoadedLiveDoc) {
					const { subType } = getCachedPageData(match.params.contentId);
					if (subType === 'live') {
						return true;
					}
				}
				return false;
			},
			onPerformanceMetricStart: ({ match }) => {
				if (match?.params?.contentId) {
					startLivePageLoadMetrics({
						userId: userId ?? undefined,
					});
				}
			},
			onRouteContinueCallback: ({ match }) =>
				new Promise<void>(async (resolve) => {
					const nextContentId = match?.params?.contentId;
					const { subType } = getCachedPageData(nextContentId);
					if (subType !== 'live') {
						return;
					}

					const draft = await getDraft(nextContentId); // This is performant as it's already cached because our callback promise retrieved CollabDraftQuery in `preloadLivePageRoute`
					const ncsProvider = getCollabEditProvider({
						contentId: nextContentId,
						cloudId,
						activationId,
						contentType: 'page',
						permissionTokenRefresh: fetchCollabPermissionToken(nextContentId, undefined),
						initialDraft: draft,
						isLivePage: true,
						presenceId: presenceIdRef.current,
						presenceActivity: hasEditPermission ? 'editor' : 'viewer',
						userId,
						fetchUsersClient,
					});

					setCollabEditProvider(ncsProvider);
					// @ts-ignore
					const hasInitialDraft = Boolean(ncsProvider?.initialDraft);
					setHasInitialDraft(hasInitialDraft);

					resolve();
				}),
		});
	}, [
		activationId,
		contentId,
		isLivePage,
		collabEditProvider,
		setCollabEditProvider,
		setHasInitialDraft,
		cloudId,
		nativeEditorComponentsPreloader,
		spaceKey,
		userId,
		hasEditPermission,
		presenceIdRef,
		fetchUsersClient,
		resetNativeCollabState,
		isFastLoadedLiveDoc,
	]);

	return {
		isTransitionPreloading,
	};
};
